import { useState, useEffect } from 'react';
import styled from 'styled-components';
import colors from '../../../styles/colors';
import { apiAdjustCredits, apiGetCreditHistory } from '../../../api/userApi';
import type { CardAccount, CreditGrant } from '../../../types/models/userCredit';

// ─── Overlay / Modal shell ────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const Modal = styled.div`
  background-color: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: gold;
  margin: 0 0 4px;
`;

const ModalSubtitle = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.45);
  margin: 0;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 20px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  font-size: 20px;
  cursor: pointer;
  line-height: 1;
  &:hover { color: #fff; }
`;

const ModalBody = styled.div`
  overflow-y: auto;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

// ─── Sezione operazione ───────────────────────────────────────────────────────

const SectionTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 12px;
`;

const BalanceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const BalanceLabel = styled.span`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.55);
`;

const BalanceValue = styled.span`
  font-size: 22px;
  font-weight: 700;
  color: #fff;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 5px;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 9px 12px;
  background-color: #111;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 5px;
  color: #fff;
  font-size: 14px;
  outline: none;
  &::placeholder { color: rgba(255, 255, 255, 0.25); }
  &:focus { border-color: rgba(255, 215, 0, 0.4); }
`;

const Textarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: 9px 12px;
  background-color: #111;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 5px;
  color: #fff;
  font-size: 14px;
  outline: none;
  resize: vertical;
  min-height: 70px;
  font-family: inherit;
  &::placeholder { color: rgba(255, 255, 255, 0.25); }
  &:focus { border-color: rgba(255, 215, 0, 0.4); }
`;

const BtnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 4px;
`;

const AddBtn = styled.button`
  padding: 10px;
  background-color: rgba(40, 167, 69, 0.15);
  color: #28a745;
  border: 1px solid rgba(40, 167, 69, 0.4);
  border-radius: 5px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background-color: rgba(40, 167, 69, 0.25); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const RemoveBtn = styled.button`
  padding: 10px;
  background-color: rgba(220, 53, 69, 0.12);
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.35);
  border-radius: 5px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background-color: rgba(220, 53, 69, 0.22); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const ErrorMsg = styled.p`
  color: #dc3545;
  font-size: 13px;
  margin: 0;
`;

const SuccessMsg = styled.p`
  color: #28a745;
  font-size: 13px;
  margin: 0;
`;

// ─── Storico ──────────────────────────────────────────────────────────────────

const Divider = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.07);
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HistoryItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 5px;
`;

const HistoryAmount = styled.span<{ positive: boolean }>`
  font-size: 15px;
  font-weight: 700;
  color: ${({ positive }) => (positive ? '#28a745' : '#dc3545')};
  flex-shrink: 0;
  min-width: 60px;
`;

const HistoryMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const HistoryNotes = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
  margin: 0 0 3px;
  word-break: break-word;
`;

const HistoryAdmin = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.35);
  margin: 0;
`;

const EmptyHistory = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
  text-align: center;
  margin: 8px 0;
`;

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  account: CardAccount;
  onClose: () => void;
  onCreditsUpdated: (accountId: number, delta: number) => void;
}

const CreditModal: React.FC<Props> = ({ account, onClose, onCreditsUpdated }) => {
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [opError, setOpError] = useState<string | null>(null);
  const [opSuccess, setOpSuccess] = useState<string | null>(null);

  const [currentBalance, setCurrentBalance] = useState(account.credits ?? 0);
  const [history, setHistory] = useState<CreditGrant[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    apiGetCreditHistory(account.user.id)
      .then((res) => { if (isMounted) setHistory(Array.isArray(res) ? res : []); })
      .catch(() => { if (isMounted) setHistory([]); })
      .finally(() => { if (isMounted) setHistoryLoading(false); });
    return () => { isMounted = false; };
  }, [account.user.id]);

  const handleSubmit = async (sign: 1 | -1) => {
    const parsed = parseInt(amount, 10);
    if (!amount || isNaN(parsed) || parsed <= 0) {
      setOpError('Inserisci un importo valido (intero positivo).');
      return;
    }
    const finalAmount = sign * parsed;
    setOpError(null);
    setOpSuccess(null);
    setIsSubmitting(true);
    try {
      const res = await apiAdjustCredits(account.user.id, finalAmount, notes.trim() || undefined);
      setCurrentBalance((prev) => prev + finalAmount);
      onCreditsUpdated(account.id, finalAmount);
      setHistory((prev) => prev ? [{ id: Date.now(), amount: finalAmount, notes: notes.trim() || null, granted_by: null, created_at: new Date().toISOString() }, ...prev] : null);
      setOpSuccess(res.message);
      setAmount('');
      setNotes('');
    } catch (err: any) {
      const msg = err?.message ?? err?.errors?.amount?.[0] ?? 'Operazione fallita.';
      setOpError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return iso;
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
        <CloseBtn onClick={onClose}>✕</CloseBtn>

        <ModalHeader>
          <ModalTitle>Gestisci Crediti</ModalTitle>
          <ModalSubtitle>{account.user.username}{account.user.nickname_forum ? ` — ${account.user.nickname_forum}` : ''}</ModalSubtitle>
        </ModalHeader>

        <ModalBody>
          {/* ── Operazione ── */}
          <div>
            <SectionTitle>Operazione</SectionTitle>
            <BalanceRow>
              <BalanceLabel>Saldo attuale:</BalanceLabel>
              <BalanceValue>{currentBalance.toLocaleString()}</BalanceValue>
            </BalanceRow>

            <div style={{ marginBottom: 10 }}>
              <FieldLabel>Importo *</FieldLabel>
              <Input
                type="number"
                min="1"
                step="1"
                placeholder="es: 50"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <FieldLabel>Note (opzionale)</FieldLabel>
              <Textarea
                placeholder="es: Premio gara predizioni"
                maxLength={500}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            {opError && <ErrorMsg>{opError}</ErrorMsg>}
            {opSuccess && <SuccessMsg>{opSuccess}</SuccessMsg>}

            <BtnRow>
              <AddBtn onClick={() => handleSubmit(1)} disabled={isSubmitting}>
                + Aggiungi
              </AddBtn>
              <RemoveBtn onClick={() => handleSubmit(-1)} disabled={isSubmitting}>
                − Rimuovi
              </RemoveBtn>
            </BtnRow>
          </div>

          <Divider />

          {/* ── Storico ── */}
          <div>
            <SectionTitle>Storico Transazioni</SectionTitle>
            {historyLoading && <EmptyHistory>Caricamento...</EmptyHistory>}
            {!historyLoading && Array.isArray(history) && (
              <HistoryList>
                {history.length === 0 && <EmptyHistory>Nessuna transazione.</EmptyHistory>}
                {history.map((grant) => (
                  <HistoryItem key={grant.id}>
                    <HistoryAmount positive={grant.amount > 0}>
                      {grant.amount > 0 ? '+' : ''}{grant.amount.toLocaleString()}
                    </HistoryAmount>
                    <HistoryMeta>
                      <HistoryNotes>{grant.notes ?? <em style={{ opacity: 0.4 }}>Nessuna nota</em>}</HistoryNotes>
                      <HistoryAdmin>
                        {grant.granted_by ? `Admin: ${grant.granted_by.name} · ` : ''}{formatDate(grant.created_at)}
                      </HistoryAdmin>
                    </HistoryMeta>
                  </HistoryItem>
                ))}
              </HistoryList>
            )}
          </div>
        </ModalBody>
      </Modal>
    </Overlay>
  );
};

export default CreditModal;
